import React, { useState } from 'react';
import {
  Button,
  Group,
  Box,
  Text,
  Flex,
  Modal,
  useMantineTheme,
  Title,
  Center,
  Stack,
  Code,
  Input,
  MultiSelect,
  Slider,
  Checkbox,
  FileInput,
  Image,
  ColorPicker,
  Select,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedState } from '@mantine/hooks';
import { IconCheck, IconTrash, IconUpload } from '@tabler/icons';
import { NavLink, useNavigate } from 'react-router-dom';
import { SubmittableTechnology, Technology } from '../../../backend/models/technology';
import { SELECTED_BLIP_WIDTH } from '../Radar';
import { addClient, addPerson, addTag, deleteTechnology, uploadLogo } from '../../Util/server';
import FloatingLabelInput from '../../Components/Input';
import RichText from '../../Components/RichText';
import { getLogoUrl } from './shared';
import { Ring } from '../../../backend/models/ring';
import { Section } from '../../../backend/models/section';
import { Tag } from '../../../backend/models/tag';
import { Client } from '../../../backend/models/client';
import { Person } from '../../../backend/models/person';

type TechnologyFormType = {
  data?: Technology;
  rings: Ring[];
  sections: Section[];
  tags: Tag[];
  clients: Client[];
  persons: Person[];
  names: string[];
  onSubmit: (values: SubmittableTechnology) => Promise<Technology>;
  buttonLabel: string;
  add: boolean;
  successMessage: string;
};

export const DEFAULT_BLIP_COLOR = '#212529';

const TechnologyForm = (props: TechnologyFormType) => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  // Checks both null and undefined
  const [randomRadial, setRandomRadial] = useState<boolean>(props.data?.radialAdjust == null);
  const [blipColorValue, setBlipColorValue] = useDebouncedState(props.data?.blipColor ?? DEFAULT_BLIP_COLOR, 50);
  const [tags, setTags] = useState<Tag[]>(props.tags);
  const [clients, setClients] = useState<Client[]>(props.clients);
  const [persons, setPersons] = useState<Person[]>(props.persons);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const form = useForm<SubmittableTechnology>({
    initialValues: {
      name: props.data?.name ?? '',
      sectionId: props.data?.sectionId ?? '',
      ringId: props.data?.ringId ?? '',
      radialAdjust: props.data?.radialAdjust ?? 50,
      blipColor: props.data?.blipColor ?? DEFAULT_BLIP_COLOR,
      tagIds: props.data?.tags?.map((t) => t.id) ?? [],
      clientIds: props.data?.clients?.map((c) => c.id) ?? [],
      superUserIds:
        props.data?.personRelations?.filter((r) => r.relationType === 'superUser').map((r) => r.personId) ?? [],
      url: props.data?.url ?? '',
      logoUrl: props.data?.logoUrl ?? '',
      partnerManagerId:
        props.data?.personRelations?.find((r) => r.relationType === 'partnerManager')?.personId ?? undefined,
      techLeadId: props.data?.personRelations?.find((r) => r.relationType === 'techLead')?.personId ?? undefined,
      contractLink: props.data?.contractLink ?? '',
      description: props.data?.description ?? '',
    },

    transformValues: (values) => ({
      name: values.name.trim(),
      sectionId: values.sectionId?.trim(),
      ringId: values.ringId?.trim(),
      radialAdjust: randomRadial ? undefined : values.radialAdjust,
      blipColor: blipColorValue,
      tagIds: values.tagIds,
      clientIds: values.clientIds,
      superUserIds: values.superUserIds,
      url: values.url?.trim(),
      logoUrl: values.logoUrl,
      partnerManagerId: values.partnerManagerId,
      techLeadId: values.techLeadId,
      contractLink: values.contractLink?.trim(),
      description: values.description,
      personRelations: values.personRelations,
    }),

    validate: {
      name: (value) =>
        !!value.trim() ? (
          props.names
            .filter((n) => n !== props.data?.name)
            .map((n) => n.toLocaleLowerCase())
            .includes(value.toLocaleLowerCase().trim()) ? (
            <>
              Technology already{' '}
              <NavLink to={`/technology/${encodeURIComponent(value.toLocaleLowerCase())}`}>exists</NavLink>
            </>
          ) : null
        ) : (
          'Name is required'
        ),
      sectionId: (value) => (!!value ? null : 'Section is required'),
      ringId: (value) => (!!value ? null : 'Ring is required'),
    },
  });

  const handleSubmit = async (values: SubmittableTechnology) => {
    setLoading(true);
    if (logoFile) {
      await uploadLogo(logoFile).then((url) => {
        values.logoUrl = url;
      });
    }
    props.onSubmit(values).then((res) => {
      navigate(`/technology/${encodeURIComponent(res.name)}`);
    });
  };

  const handleDelete = () => {
    setDeleting(true);
    setDeleting(false);
    // id is always present if we're in Update mode
    if (props.data?.id) {
      deleteTechnology(props.data?.id).then(() => {
        setDeleted(true);
        setRemoveModalOpen(false);
        setMessage('Technology deleted successfully');
      });
    }
  };

  const createTag = (name: string) => {
    setLoading(true);
    async function doCreate() {
      addTag(name)
        .then((res) => {
          form.setFieldValue('tagIds', [...(form.values.tagIds ?? []), res.id]);
          setTags((oldTags) => [...oldTags, res]);
          setLoading(false);
          setMessage('Tag created successfully');
        })
        .catch(() => {
          setLoading(false);
        });
    }

    doCreate();
  };

  const createClient = (name: string) => {
    setLoading(true);
    async function doCreate() {
      addClient({ name })
        .then((res) => {
          form.setFieldValue('clientIds', [...(form.values.clientIds ?? []), res.id]);
          setClients((oldClients) => [...oldClients, res]);
          setLoading(false);
          setMessage('Client created successfully');
        })
        .catch(() => {
          setLoading(false);
        });
    }

    doCreate();
  };

  const createPerson = (name: string, callback: (id: string) => void) => {
    setLoading(true);
    async function doCreate() {
      addPerson(name)
        .then((res) => {
          callback(res.id);
          setPersons((oldPersons) => [...oldPersons, res]);
          setLoading(false);
          setMessage('Person created successfully');
        })
        .catch(() => {
          setLoading(false);
        });
    }

    doCreate();
  };

  return (
    <Box sx={{ maxWidth: '100%', width: 600 }} mx="auto">
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <FloatingLabelInput
          label="Technology name"
          placeholder="Add name"
          required
          withAsterisk
          disabled={deleted}
          {...form.getInputProps('name')}
        />
        <Select
          data={props.sections.map((section) => {
            return { value: section.id, label: section.name };
          })}
          label="Section"
          placeholder="Select section"
          mt={'xl'}
          withAsterisk
          required
          disabled={deleted}
          {...form.getInputProps('sectionId')}
        />
        <Select
          data={props.rings.map((ring) => {
            return { value: ring.id, label: ring.name };
          })}
          label="Ring"
          placeholder="Select ring"
          mt={'xl'}
          withAsterisk
          required
          disabled={deleted}
          {...form.getInputProps('ringId')}
        />
        <Text mt={'xl'} fz="sm" fw={500} ta="left">
          {'Ring radial distance'}
        </Text>
        <Stack sx={{ gap: 8 }} pt={8}>
          <Checkbox
            checked={randomRadial}
            disabled={deleted}
            onChange={() => setRandomRadial((oldVal) => !oldVal)}
            label={
              <Group sx={{ gap: 4 }}>
                <Text>{'Random'}</Text>
                <Text c="dimmed">{'(uncheck to control ring placement)'}</Text>
              </Group>
            }
          />
          <Slider
            step={25}
            label={null}
            disabled={randomRadial}
            defaultValue={50}
            marks={[
              { value: 25, label: 'Closer to center' },
              { value: 75, label: 'Further from center' },
            ]}
            {...form.getInputProps('radialAdjust')}
          />
        </Stack>
        <Group align={'flex-end'} spacing={'sm'}>
          <Text mt={40} fz="sm" fw={500} ta="left">
            {'Blip color'}
          </Text>
          <div
            style={{
              borderRadius: '50%',
              width: SELECTED_BLIP_WIDTH,
              height: SELECTED_BLIP_WIDTH,
              backgroundColor: blipColorValue,
            }}
          ></div>
        </Group>
        <ColorPicker fullWidth format="hex" mt={'xs'} value={blipColorValue} onChange={setBlipColorValue} />
        <MultiSelect
          data={tags.map((v) => ({
            value: v.id,
            label: v.name,
          }))}
          label="Tags"
          placeholder="e.g Reviews or Cookies"
          mt={'xl'}
          disabled={deleted}
          creatable
          searchable
          getCreateLabel={(query) => `+ Add '${query}'`}
          onCreate={(query) => {
            createTag(query.trim());
            return null;
          }}
          {...form.getInputProps('tagIds')}
        />
        <MultiSelect
          data={clients.map((v) => ({
            value: v.id,
            label: v.name,
          }))}
          label="Clients"
          placeholder="e.g Kicks or Nordic Nest"
          mt={'xl'}
          disabled={deleted}
          creatable
          searchable
          getCreateLabel={(query) => `+ Add '${query}'`}
          onCreate={(query) => {
            createClient(query.trim());
            return null;
          }}
          {...form.getInputProps('clientIds')}
        />
        <FloatingLabelInput
          label="Homepage URL"
          placeholder="https://..."
          mt={'xl'}
          disabled={deleted}
          {...form.getInputProps('url')}
        />
        {form.values['logoUrl'] ? (
          <>
            <Text mt={'xl'} fz="sm" fw={500} ta="left">
              {'Logo'}
            </Text>
            <Group>
              <Image src={getLogoUrl(form.values['logoUrl'])} width={50} height={50} fit="contain" />
              <Text ml={0}>{form.values['logoUrl']}</Text>
              <Button
                color={'red'}
                variant={'outline'}
                leftIcon={<IconTrash size={16} />}
                onClick={() => form.setFieldValue('logoUrl', undefined)}
              >
                {'Remove logo'}
              </Button>
            </Group>
          </>
        ) : (
          <FileInput
            mt={'xl'}
            label="Logo"
            placeholder="Upload logo"
            value={logoFile}
            onChange={setLogoFile}
            disabled={deleted}
            icon={<IconUpload size={14} />}
          />
        )}
        <Select
          data={persons.map((person) => {
            return { value: person.id, label: person.name };
          })}
          label="Partner manager"
          placeholder="Partner manager"
          mt={'xl'}
          disabled={deleted}
          creatable
          searchable
          clearable
          getCreateLabel={(query) => `+ Add '${query}'`}
          onCreate={(query) => {
            createPerson(query.trim(), (id: string) => form.setFieldValue('partnerManagerId', id));
            return null;
          }}
          {...form.getInputProps('partnerManagerId')}
        />
        <Select
          data={persons.map((person) => {
            return { value: person.id, label: person.name };
          })}
          label="Tech lead"
          placeholder="Tech lead"
          mt={'xl'}
          disabled={deleted}
          creatable
          searchable
          clearable
          getCreateLabel={(query) => `+ Add '${query}'`}
          onCreate={(query) => {
            createPerson(query.trim(), (id: string) => form.setFieldValue('techLeadId', id));
            return null;
          }}
          {...form.getInputProps('techLeadId')}
        />
        <MultiSelect
          data={persons.map((v) => ({
            value: v.id,
            label: v.name,
          }))}
          label="Super users"
          placeholder="People at Avensia well versed within the technology"
          mt={'xl'}
          disabled={deleted}
          creatable
          searchable
          getCreateLabel={(query) => `+ Add '${query}'`}
          onCreate={(query) => {
            createPerson(query.trim(), (id: string) => {
              form.setFieldValue('superUserIds', [...(form.values.superUserIds ?? []), id]);
            });
            return null;
          }}
          {...form.getInputProps('superUserIds')}
        />
        <FloatingLabelInput
          label="Contract link"
          placeholder="Add link to contract"
          mt={'xl'}
          disabled={deleted}
          {...form.getInputProps('contractLink')}
        />
        <RichText label="Description" disabled={deleted} {...form.getInputProps('description')} />

        <Flex justify={'space-between'} align={'center'} mt="md">
          {
            <Group align={'center'}>
              {message && (
                <>
                  <Text color={'green'}>{message}</Text>
                  <IconCheck color={'green'} />
                </>
              )}
            </Group>
          }
          <Group position="right">
            {!props.add && (
              <Button color="red" onClick={() => setRemoveModalOpen(true)} disabled={deleted}>
                Remove
              </Button>
            )}
            <Button type="submit" loading={loading} disabled={deleted}>
              {props.buttonLabel}
            </Button>
          </Group>
        </Flex>
      </form>

      <Modal
        opened={removeModalOpen}
        onClose={() => {
          setRemoveModalOpen(false);
          setConfirmationText('');
        }}
        centered
        size={'lg'}
        title={<Title order={4}>{'Remove technology from radar?'}</Title>}
        overlayColor={theme.colors.gray[2]}
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <Center>
          <Stack mt="lg" spacing={'sm'}>
            <Text>{`Are you sure you want to remove ${props.data?.name} as a technology?`}</Text>
            <div>
              <Text display={'inline'}>{'If so, type'}</Text>
              <Code>{props.data?.name}</Code>
              <Text display={'inline'}>{'in the input below and then click the button'}</Text>
            </div>
            <Group mt={'xs'}>
              <Input
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.currentTarget.value)}
                style={{ display: 'flex', flexGrow: 1 }}
              />
              <Button
                color="red"
                disabled={confirmationText !== props.data?.name}
                onClick={() => handleDelete()}
                loading={deleting}
                loaderPosition={'center'}
              >
                YES, I'M SURE
              </Button>
            </Group>
          </Stack>
        </Center>
      </Modal>
    </Box>
  );
};

export default TechnologyForm;
